import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { ApprovalStatus, UserRole } from "@/hooks/useUserRole";
import {
  canSwitchWorkspace,
  hasApprovedPfaWorkspace,
  hasApprovedPartnerWorkspace,
  hasPfaWorkspace,
  pickPrimaryRoleRow,
  type UserRoleRow,
} from "@/lib/userRoles";

export type { UserRoleRow };

function mapRoleRows(
  data: Array<{
    role: string;
    approval_status: string;
    is_banned: boolean | null;
    ban_reason: string | null;
  }> | null,
): UserRoleRow[] {
  return (data ?? []).map((row) => ({
    role: row.role as UserRole,
    approval_status: row.approval_status as ApprovalStatus,
    is_banned: row.is_banned ?? false,
    ban_reason: row.ban_reason ?? null,
  }));
}

export function useUserRoles(userId?: string) {
  const [roleRows, setRoleRows] = useState<UserRoleRow[]>([]);
  const [isPartner, setIsPartner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(Boolean(userId));
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(userId ? true : null);

  const fetchUserRolesForUser = useCallback(async (targetUserId: string, options?: { background?: boolean }) => {
    if (!options?.background) {
      setLoading(true);
    }
    try {
      const [rolesResult, profileResult] = await Promise.all([
        supabase
          .from("user_roles")
          .select("role, approval_status, is_banned, ban_reason")
          .eq("user_id", targetUserId),
        supabase.from("pfa_profiles").select("is_partner").eq("user_id", targetUserId).maybeSingle(),
      ]);

      if (rolesResult.error) {
        console.error("Error fetching user roles:", rolesResult.error);
        setRoleRows([]);
      } else {
        setRoleRows(mapRoleRows(rolesResult.data));
      }

      if (profileResult.error) {
        console.error("Error fetching partner profile flag:", profileResult.error);
        setIsPartner(false);
      } else {
        setIsPartner(profileResult.data?.is_partner === true);
      }
    } catch (error) {
      console.error("Error in useUserRoles:", error);
      setRoleRows([]);
      setIsPartner(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      setAuthReady(true);
      setIsAuthenticated(true);
      void fetchUserRolesForUser(userId);
      return;
    }

    let active = true;

    const applySession = (session: { user: { id: string } } | null) => {
      if (!active) return;
      setAuthReady(true);
      setIsAuthenticated(!!session?.user);
      if (session?.user) {
        void fetchUserRolesForUser(session.user.id);
      } else {
        setRoleRows([]);
        setIsPartner(false);
        setLoading(false);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "INITIAL_SESSION") {
        applySession(session);
        return;
      }

      if (event === "SIGNED_IN") {
        if (session?.user) {
          setIsAuthenticated(true);
          setAuthReady(true);
          void fetchUserRolesForUser(session.user.id);
        }
        return;
      }

      if (event === "TOKEN_REFRESHED") {
        if (session?.user) {
          setIsAuthenticated(true);
          setAuthReady(true);
        }
        return;
      }

      if (event === "SIGNED_OUT") {
        setRoleRows([]);
        setIsPartner(false);
        setIsAuthenticated(false);
        setAuthReady(true);
        setLoading(false);
      }
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [fetchUserRolesForUser, userId]);

  const partnerOptions = useMemo(() => ({ isPartner }), [isPartner]);
  const primaryRoleRow = useMemo(
    () => pickPrimaryRoleRow(roleRows, partnerOptions),
    [roleRows, partnerOptions],
  );

  const reload = useCallback(async () => {
    if (userId) {
      await fetchUserRolesForUser(userId, { background: true });
      return;
    }
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.user) {
      await fetchUserRolesForUser(session.user.id, { background: true });
    } else {
      setRoleRows([]);
      setIsPartner(false);
      setIsAuthenticated(false);
      setAuthReady(true);
      setLoading(false);
    }
  }, [fetchUserRolesForUser, userId]);

  return {
    roleRows,
    isPartner,
    role: primaryRoleRow?.role ?? null,
    approvalStatus: primaryRoleRow?.approval_status ?? null,
    isBanned: primaryRoleRow?.is_banned ?? false,
    banReason: primaryRoleRow?.ban_reason ?? null,
    hasApprovedPfa: hasApprovedPfaWorkspace(roleRows),
    hasPfaWorkspace: hasPfaWorkspace(roleRows),
    hasApprovedPartner: hasApprovedPartnerWorkspace(roleRows, partnerOptions),
    canSwitchWorkspace: canSwitchWorkspace(roleRows, partnerOptions),
    loading,
    authReady,
    isAuthenticated,
    reload,
  };
}
